import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { History, Calendar, CreditCard, Package, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ServiceHistory() {
  const [history, setHistory] = useState({ bookings: [], transactions: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API}/service-history`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setHistory(data);
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const allItems = [
    ...history.bookings.map(b => ({ ...b, type: 'booking' })),
    ...history.transactions.map(t => ({ ...t, type: 'transaction' }))
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const filteredItems = activeTab === 'all' 
    ? allItems 
    : allItems.filter(item => item.type === activeTab);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="service-history-page">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">Service History</h1>
        <p className="text-gray-400">View your completed services and payment history</p>
      </motion.div>

      {/* Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex gap-2 mb-8"
      >
        {[
          { id: 'all', label: 'All Activity' },
          { id: 'booking', label: 'Services' },
          { id: 'transaction', label: 'Payments' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border ${
              activeTab === tab.id
                ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                : 'bg-[#0D1224] text-gray-400 border-blue-500/10 hover:text-white hover:bg-blue-500/5'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </motion.div>

      {/* History List */}
      {filteredItems.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-16"
        >
          <History className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No history yet</h3>
          <p className="text-gray-400">Your completed services and payments will appear here.</p>
        </motion.div>
      ) : (
        <div className="space-y-4">
          {filteredItems.map((item, idx) => (
            <motion.div
              key={item.booking_id || item.transaction_id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="flex items-start gap-4 p-6 rounded-xl border group"
              style={{ background: '#0D1224', border: '1px solid rgba(99, 130, 255, 0.12)' }}
              data-testid={`history-item-${idx}`}
            >
              {/* Icon */}
              <div className={`
                w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 border
                ${item.type === 'booking' ? 'bg-green-500/10 border-green-500/20' : 'bg-blue-500/10 border-blue-500/20'}
              `}>
                {item.type === 'booking' ? (
                  <CheckCircle2 className="w-6 h-6 text-green-400" />
                ) : (
                  <CreditCard className="w-6 h-6 text-blue-400" />
                )}
              </div>

              {/* Content */}
              <div className="flex-1">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-white font-medium mb-1 group-hover:text-blue-400 transition-colors">
                      {item.type === 'booking' 
                        ? `Service Completed: ${item.package_id}`
                        : `Payment: ${item.package_id}`
                      }
                    </h3>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-blue-400" />
                        {formatDate(item.created_at)}
                      </span>
                      {item.type === 'transaction' && (
                        <span className="flex items-center gap-1">
                          <Package className="w-3 h-3 text-blue-400" />
                          {item.currency?.toUpperCase() || 'USD'}
                        </span>
                      )}
                    </div>
                  </div>

                  {item.type === 'transaction' && (
                    <div className="text-right">
                      <span className="text-xl font-semibold text-green-400">
                        ₹{item.amount?.toFixed(2)}
                      </span>
                      <p className="text-xs text-gray-500">Paid</p>
                    </div>
                  )}
                </div>

                {item.address && (
                  <p className="text-gray-400 text-sm mt-2">
                    Location: {item.address}
                  </p>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Summary Stats */}
      {allItems.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          {[
            { 
              label: 'Total Services', 
              value: history.bookings.length,
              icon: CheckCircle2,
              color: 'text-green-400 bg-green-400/10 border-green-500/20'
            },
            { 
              label: 'Total Payments', 
              value: history.transactions.length,
              icon: CreditCard,
              color: 'text-blue-400 bg-blue-500/10 border-blue-500/20 shadow-sm'
            },
            { 
              label: 'Total Spent', 
              value: `₹${history.transactions.reduce((sum, t) => sum + (t.amount || 0), 0).toFixed(2)}`,
              icon: Package,
              color: 'text-blue-400 bg-blue-500/10 border-blue-500/20 shadow-sm'
            },
            { 
              label: 'Active Since', 
              value: allItems.length > 0 
                ? new Date(allItems[allItems.length - 1].created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                : 'N/A',
              icon: Calendar,
              color: 'text-blue-400 bg-blue-500/10 border-blue-500/20 shadow-sm'
            }
          ].map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <div
                key={stat.label}
                className="p-4 rounded-xl border neo-card"
                style={{ background: '#0D1224', borderColor: 'rgba(99, 130, 255, 0.12)' }}
              >
                <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center mb-3 border`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="text-xl font-bold text-white">{stat.value}</div>
                <div className="text-xs text-gray-500">{stat.label}</div>
              </div>
            );
          })}
        </motion.div>
      )}
    </div>
  );
}
